import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Avatar, Stack, Box, Popper, ClickAwayListener, Grid, Divider } from '@mui/material';
import classnames from 'classnames';
import { ReactComponent as UsFlag } from 'assets/images/us-flag.svg';
import { ReactComponent as MxFlag } from 'assets/images/mx-flag.svg';
import { ReactComponent as BrFlag } from 'assets/images/br-flag.svg';
import { ReactComponent as FrFlag } from 'assets/images/fr-flag.svg';
import { ReactComponent as ZhFlag } from 'assets/images/zh-flag.svg';
import { ReactComponent as RuFlag } from 'assets/images/ru-flag.svg';
import { ReactComponent as HiFlag } from 'assets/images/hi-flag.svg';
import { ReactComponent as ArFlag } from 'assets/images/ar-flag.svg';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import { useTranslation } from 'react-i18next';

import { ChevronRightIcon, CrownIcon, GearIcon, HelpIcon, LogOutIcon } from 'assets';
import { ConnectionTestDialog } from 'components/ConnectionTest/ConnectionTestDialog';
import { Transitions } from 'components';
import { selectUser } from 'store/user';
import { useAppSelector } from 'store';
import { IApiRequests, useApiRequests, useAzureAsset, useThemePreference, useUser } from 'hooks';
import { ApiUser } from 'types';
import { getFullName } from 'utils';
import { IUserContext } from 'context';
import { isHipaaLink } from 'config/productProfile';
import { getVisibleThemePreference, renderThemeIcon } from 'theme';

import styles from './ProfileSection.module.scss';

export function ProfileSection() {
  const { t: tAuth, i18n } = useTranslation('auth');
  const { t: tProviderRoom } = useTranslation('providerRoom');
  const navigate = useNavigate();
  const user: ApiUser | null = useAppSelector(selectUser);
  const { logout }: IUserContext = useUser();
  const { updateClinicUser }: IApiRequests = useApiRequests();

  const [themeAnchorEl, setThemeAnchorEl] = useState<HTMLElement | null>(null);
  const isThemeMenuOpen = Boolean(themeAnchorEl);
  const [languageAnchorEl, setLanguageAnchorEl] = useState<HTMLElement | null>(null);
  const isLanguageMenuOpen = Boolean(languageAnchorEl);
  const themeCloseTimeoutRef = useRef<number | null>(null);
  const languageCloseTimeoutRef = useRef<number | null>(null);

  const selectedLanguage =
    i18n.resolvedLanguage === 'es' ||
    i18n.resolvedLanguage === 'br' ||
    i18n.resolvedLanguage === 'fr' ||
    i18n.resolvedLanguage === 'zh' ||
    i18n.resolvedLanguage === 'ru' ||
    i18n.resolvedLanguage === 'hi' ||
    i18n.resolvedLanguage === 'ar'
      ? i18n.resolvedLanguage
      : 'en';

  const languageOptions: Array<{
    code: 'en' | 'es' | 'br' | 'fr' | 'zh' | 'ru' | 'hi' | 'ar';
    label: string;
    Flag: React.FC<React.SVGProps<SVGSVGElement>>;
  }> = [
    { code: 'en', label: 'English', Flag: UsFlag },
    { code: 'es', label: 'Español', Flag: MxFlag },
    { code: 'br', label: 'Português', Flag: BrFlag },
    { code: 'fr', label: 'Français', Flag: FrFlag },
    { code: 'zh', label: '中文', Flag: ZhFlag },
    { code: 'ru', label: 'Русский', Flag: RuFlag },
    { code: 'hi', label: 'हिन्दी', Flag: HiFlag },
    { code: 'ar', label: 'العربية', Flag: ArFlag },
  ];

  const selectedLanguageOption =
    languageOptions.find((option) => option.code === selectedLanguage) || languageOptions[0];

  const handleLanguageChange = (language: 'en' | 'es' | 'br' | 'fr' | 'zh' | 'ru' | 'hi' | 'ar') => {
    i18n.changeLanguage(language);
    setLanguageAnchorEl(null);
    if (user) {
      updateClinicUser({ languageCode: language }).catch(() => undefined);
    }
  };

  const [open, setOpen] = useState(false);
  const [connectionTestOpen, setConnectionTestOpen] = useState(false);
  const { themePreference, setThemePreference } = useThemePreference();
  const themePreferenceForUi = getVisibleThemePreference(themePreference);
  const prevOpen = useRef(open);
  const anchorRef = useRef<HTMLDivElement>(null);
  const avatarUrl = useAzureAsset(user?.linkToProfilePicture);
  const isUnavailable = Boolean(user?.clinicUser?.unavailable);

  function handleSignOut(): void {
    navigate('/');
    logout();
  }

  function handleHelp(): void {
    window.open('https://support.hipaalink.net/', '_blank', 'noopener,noreferrer');
    setOpen(false);
  }

  function handleConnectionTest(): void {
    setOpen(false);
    setConnectionTestOpen(true);
  }

  const handleToggle = () => {
    setOpen((prevOpen) => {
      if (prevOpen) {
        clearThemeCloseTimeout();
        clearLanguageCloseTimeout();
        setThemeAnchorEl(null);
        setLanguageAnchorEl(null);
      }

      return !prevOpen;
    });
  };

  const clearThemeCloseTimeout = () => {
    if (themeCloseTimeoutRef.current) {
      window.clearTimeout(themeCloseTimeoutRef.current);
      themeCloseTimeoutRef.current = null;
    }
  };

  const clearLanguageCloseTimeout = () => {
    if (languageCloseTimeoutRef.current) {
      window.clearTimeout(languageCloseTimeoutRef.current);
      languageCloseTimeoutRef.current = null;
    }
  };

  const closeThemeMenuDelayed = () => {
    clearThemeCloseTimeout();
    themeCloseTimeoutRef.current = window.setTimeout(() => {
      setThemeAnchorEl(null);
    }, 160);
  };

  const closeLanguageMenuDelayed = () => {
    clearLanguageCloseTimeout();
    languageCloseTimeoutRef.current = window.setTimeout(() => {
      setLanguageAnchorEl(null);
    }, 160);
  };

  const handleClose = () => {
    setOpen(false);
    clearThemeCloseTimeout();
    clearLanguageCloseTimeout();
    setThemeAnchorEl(null);
    setLanguageAnchorEl(null);
  };

  const handleSettingsClick = (): void => {
    setOpen(false);
    navigate('../settings');
  };

  const handleThemeChange = (mode: typeof themePreference) => {
    setThemePreference(mode);
    setThemeAnchorEl(null);
  };

  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current && anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  useEffect(() => {
    return () => {
      clearThemeCloseTimeout();
      clearLanguageCloseTimeout();
    };
  }, []);

  return (
    <>
      <div onClick={handleToggle} ref={anchorRef} className={styles.profile}>
        <Grid className={styles.texts}>
          <Typography component='p' variant='body2' className={styles.name}>
            {getFullName(user)}
          </Typography>

          <Typography component='p' variant='body1' color='text.secondary'>
            {user?.clinicUser.organization}
          </Typography>

          <CrownIcon className={styles.crownIcon} />
        </Grid>

        <Box className={styles.profilePictureWrapper}>
          <Avatar className={styles.profilePicture} src={avatarUrl} />
          {user && (
            <span
              className={classnames(styles.availabilityDot, { [styles.availabilityDotUnavailable]: isUnavailable })}
            />
          )}
        </Box>

        <ChevronRightIcon className={styles.arrowIcon} style={{ transform: open ? 'rotate(180deg)' : '' }} />
      </div>
      <Popper
        placement='bottom-end'
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <ClickAwayListener onClickAway={handleClose}>
              <Box className={styles.menu}>
                <Stack className={styles.item} alignItems='flex-start'>
                  <Button disableRipple={true} className={styles.menuButton} onClick={handleSettingsClick}>
                    <Box className={styles.menuButtonInner}>
                      <Box className={styles.menuButtonLeft}>
                        <GearIcon color='primary' />
                        <span>{tAuth('profileSectionMenu.settings')}</span>
                      </Box>
                      <Box className={styles.menuButtonRight}>
                        <span className={styles.arrowSpacer} />
                      </Box>
                    </Box>
                  </Button>
                </Stack>

                <Stack
                  className={styles.item}
                  alignItems='flex-start'
                  onMouseEnter={(e) => {
                    if (!open) return;
                    clearThemeCloseTimeout();
                    setLanguageAnchorEl(null);
                    setThemeAnchorEl(e.currentTarget);
                  }}
                  onMouseLeave={closeThemeMenuDelayed}
                >
                  <Button disableRipple={true} className={styles.menuButton}>
                    <Box className={styles.menuButtonInner}>
                      <Box className={styles.menuButtonLeft}>
                        {renderThemeIcon(themePreferenceForUi, styles.themeMenuIcon)}
                        <span>{tAuth('profileSectionMenu.theme')}</span>
                      </Box>
                      <Box className={styles.menuButtonRight}>
                        <span className={styles.menuButtonValueLabel}>
                          {tProviderRoom(themePreferenceForUi, { defaultValue: themePreferenceForUi })}
                        </span>
                        <KeyboardArrowLeftIcon className={styles.submenuArrowIcon} />
                      </Box>
                    </Box>
                  </Button>
                </Stack>

                <Stack
                  className={styles.item}
                  alignItems='flex-start'
                  onMouseEnter={(e) => {
                    if (!open) return;
                    clearLanguageCloseTimeout();
                    setThemeAnchorEl(null);
                    setLanguageAnchorEl(e.currentTarget);
                  }}
                  onMouseLeave={closeLanguageMenuDelayed}
                >
                  <Button disableRipple className={styles.menuButton}>
                    <Box className={styles.menuButtonInner}>
                      <Box className={styles.menuButtonLeft}>
                        <selectedLanguageOption.Flag className={styles.languageFlag} />
                        <span>{tAuth('profileSectionMenu.language')}</span>
                      </Box>
                      <Box className={styles.menuButtonRight}>
                        <span className={styles.menuButtonValueLabel}>{selectedLanguageOption.label}</span>
                        <KeyboardArrowLeftIcon className={styles.submenuArrowIcon} />
                      </Box>
                    </Box>
                  </Button>
                </Stack>

                <Divider className={styles.menuDivider} />

                {isHipaaLink && (
                  <Stack className={styles.item} alignItems='flex-start'>
                    <Button disableRipple={true} className={styles.menuButton} onClick={handleHelp}>
                      <Box className={styles.menuButtonInner}>
                        <Box className={styles.menuButtonLeft}>
                          <HelpIcon color='primary' />
                          <span>{tAuth('profileSectionMenu.help')}</span>
                        </Box>
                        <Box className={styles.menuButtonRight}>
                          <span className={styles.arrowSpacer} />
                        </Box>
                      </Box>
                    </Button>
                  </Stack>
                )}

                <Stack className={styles.item} alignItems='flex-start'>
                  <Button disableRipple={true} className={styles.menuButton} onClick={handleConnectionTest}>
                    <Box className={styles.menuButtonInner}>
                      <Box className={styles.menuButtonLeft}>
                        <NetworkCheckIcon color='primary' sx={{ width: 20, height: 20 }} />
                        <span>{tProviderRoom('connectionTest.menuLabel')}</span>
                      </Box>
                      <Box className={styles.menuButtonRight}>
                        <span className={styles.arrowSpacer} />
                      </Box>
                    </Box>
                  </Button>
                </Stack>

                {isHipaaLink && <Divider className={styles.menuDivider} />}

                <Stack className={styles.item} alignItems='flex-start'>
                  <Button disableRipple={true} className={styles.menuButton} onClick={handleSignOut}>
                    <Box className={styles.menuButtonInner}>
                      <Box className={styles.menuButtonLeft}>
                        <LogOutIcon color='primary' />
                        <span>{tAuth('profileSectionMenu.logOut')}</span>
                      </Box>
                      <Box className={styles.menuButtonRight}>
                        <span className={styles.arrowSpacer} />
                      </Box>
                    </Box>
                  </Button>
                </Stack>
              </Box>
            </ClickAwayListener>
          </Transitions>
        )}
      </Popper>

      <Popper
        open={isThemeMenuOpen}
        anchorEl={themeAnchorEl}
        placement='left-start'
        disablePortal={false}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [-4, 0],
            },
          },
        ]}
      >
        <Box className={styles.subMenu} onMouseEnter={clearThemeCloseTimeout} onMouseLeave={closeThemeMenuDelayed}>
          <Button
            className={`${styles.menuButton} ${themePreferenceForUi === 'light' ? styles.selectedSubMenuButton : ''}`}
            onClick={() => handleThemeChange('light')}
            sx={{
              justifyContent: 'flex-start',
              px: 1.5,
              py: 0.75,
              minWidth: 0,
            }}
          >
            <Box className={styles.themeMenuLeft}>
              {renderThemeIcon('light')}
              <span>{tProviderRoom('light', { defaultValue: 'Light' })}</span>
            </Box>
          </Button>
          <Button
            className={`${styles.menuButton} ${themePreferenceForUi === 'dark' ? styles.selectedSubMenuButton : ''}`}
            onClick={() => handleThemeChange('dark')}
            sx={{
              justifyContent: 'flex-start',
              px: 1.5,
              py: 0.75,
              minWidth: 0,
            }}
          >
            <Box className={styles.themeMenuLeft}>
              {renderThemeIcon('dark')}
              <span>{tProviderRoom('dark', { defaultValue: 'Dark' })}</span>
            </Box>
          </Button>
          <Button
            className={`${styles.menuButton} ${themePreferenceForUi === 'system' ? styles.selectedSubMenuButton : ''}`}
            onClick={() => handleThemeChange('system')}
            sx={{
              justifyContent: 'flex-start',
              px: 1.5,
              py: 0.75,
              minWidth: 0,
            }}
          >
            <Box className={styles.themeMenuLeft}>
              {renderThemeIcon('system')}
              <span>{tProviderRoom('system', { defaultValue: 'System' })}</span>
            </Box>
          </Button>
          <Button
            className={`${styles.menuButton} ${themePreferenceForUi === 'calm' ? styles.selectedSubMenuButton : ''}`}
            onClick={() => handleThemeChange('calm')}
            sx={{
              justifyContent: 'flex-start',
              px: 1.5,
              py: 0.75,
              minWidth: 0,
            }}
          >
            <Box className={styles.themeMenuLeft}>
              {renderThemeIcon('calm')}
              <span>{tProviderRoom('calm', { defaultValue: 'Calm' })}</span>
            </Box>
          </Button>
          <Button
            className={`${styles.menuButton} ${themePreferenceForUi === 'nature' ? styles.selectedSubMenuButton : ''}`}
            onClick={() => handleThemeChange('nature')}
            sx={{
              justifyContent: 'flex-start',
              px: 1.5,
              py: 0.75,
              minWidth: 0,
            }}
          >
            <Box className={styles.themeMenuLeft}>
              {renderThemeIcon('nature')}
              <span>{tProviderRoom('nature', { defaultValue: 'Nature' })}</span>
            </Box>
          </Button>
        </Box>
      </Popper>

      <Popper
        open={isLanguageMenuOpen}
        anchorEl={languageAnchorEl}
        placement='left-start'
        disablePortal={false}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [-4, 0],
            },
          },
        ]}
      >
        <Box
          className={styles.subMenu}
          onMouseEnter={clearLanguageCloseTimeout}
          onMouseLeave={closeLanguageMenuDelayed}
        >
          {languageOptions.map((option) => {
            const isSelected = selectedLanguage === option.code;

            return (
              <Button
                key={option.code}
                className={`${styles.menuButton} ${styles.languageMenuButton} ${
                  isSelected ? styles.selectedSubMenuButton : ''
                }`}
                onClick={() => handleLanguageChange(option.code)}
                sx={{
                  justifyContent: 'flex-start',
                  px: 1.5,
                  py: 0.75,
                  minWidth: 0,
                }}
              >
                <Box className={styles.languageMenuLeft}>
                  <option.Flag className={styles.languageMenuItemIcon} />
                  <span>{option.label}</span>
                </Box>
              </Button>
            );
          })}
        </Box>
      </Popper>

      <ConnectionTestDialog open={connectionTestOpen} onClose={() => setConnectionTestOpen(false)} />
    </>
  );
}
